import { isPlottablePoint } from './markers';
import type { MapBounds, MapPoint } from './types';

/**
 * A Boundary's rings, as `src/features/fields/schemas.ts` normalises them at the parse boundary:
 * ring → point. Declared structurally rather than imported from the feature, because `lib` may
 * import only `lib` and `types`.
 *
 * **One** level of nesting, unlike the admin twin's multipolygon `MapPoint[][][]`: the tracking
 * contract sends a GeoJSON `Polygon`, exterior ring first, so a Field is one polygon and there is no
 * multipolygon level to flatten across.
 */
export type BoundaryRings = readonly (readonly MapPoint[])[];

/** A ring encloses ground when it has three plottable points. Google closes it implicitly, so a
 *  ring whose last point repeats its first — which is exactly what PostGIS returns — is fine. */
const isDrawableRing = (ring: readonly MapPoint[]) =>
  ring.length >= 3 && ring.every(isPlottablePoint);

/**
 * A Boundary's rings, or `null` when the Boundary is not wholly drawable.
 *
 * **All-or-nothing, deliberately.** Dropping bad rings individually looks kinder and is wrong twice
 * over: a Field whose *outer* ring is malformed but whose Interior shape survives would render the
 * hole as the Field, and a dropped Interior shape silently paints untouched ground as Field. Rings
 * carry no marker saying which they are, so the only honest reading of "malformed or incomplete
 * boundary data" (113586 §1f) is to skip the Field and say so.
 */
function drawableRings(boundary: BoundaryRings): MapPoint[][] | null {
  if (boundary.length === 0 || !boundary.every(isDrawableRing)) return null;
  return boundary.map((ring) => ring.map(({ lat, lon }) => ({ lat, lon })));
}

/** The tight box around every point of every ring, or `null` when there is nothing to bound. */
export function boundsOfRings(rings: BoundaryRings): MapBounds | null {
  const points = rings.flat();
  if (points.length === 0) return null;

  return points.reduce<MapBounds>(
    (box, { lat, lon }) => ({
      north: Math.max(box.north, lat),
      south: Math.min(box.south, lat),
      east: Math.max(box.east, lon),
      west: Math.min(box.west, lon),
    }),
    { north: -90, south: 90, east: -180, west: 180 },
  );
}

/** The box spanning every box given, or `null` for an empty list. */
export function unionBounds(boxes: readonly MapBounds[]): MapBounds | null {
  return boxes.reduce<MapBounds | null>(
    (acc, box) =>
      acc === null
        ? box
        : {
            north: Math.max(acc.north, box.north),
            south: Math.min(acc.south, box.south),
            east: Math.max(acc.east, box.east),
            west: Math.min(acc.west, box.west),
          },
    null,
  );
}

/**
 * Do two boxes share any ground? Containment counts (zoomed deep inside a Field), and so does a
 * shared edge — an adjacent Field must never be culled for touching the viewport's rim (113586 §1c).
 *
 * ponytail: no antimeridian wrap. Every location in this product is inside the USA (113587 §2b), so
 * a box cannot straddle ±180; add the split if that ever stops being true.
 */
export function boundsIntersect(a: MapBounds, b: MapBounds): boolean {
  return a.west <= b.east && a.east >= b.west && a.south <= b.north && a.north >= b.south;
}

/** A Field we can draw: the payload's own record plus the geometry derived from it once. */
export type DrawableField<T> = T & { rings: MapPoint[][]; bounds: MapBounds };

/**
 * Split the Grower's Fields into the ones we draw and the ones we cannot.
 *
 * 113586 §1f: a malformed or incomplete Boundary **skips that one Field** — every other one renders,
 * and the caller names the casualties in a non-blocking notice.
 *
 * This is the **client** half of §1f, and it is not redundant with the server's `meta.skipped`. The
 * zod schema deliberately does not do this job: `boundary` parses structurally, so an empty ring
 * list or a two-point ring passes validation and is only undrawable *geometrically*. The page unions
 * this result with `meta.skipped` — the Fields the server could not serialise at all — into one
 * notice.
 */
export function partitionDrawableFields<T extends { boundary: BoundaryRings }>(
  fields: readonly T[],
): { drawable: DrawableField<T>[]; skipped: T[] } {
  const drawable: DrawableField<T>[] = [];
  const skipped: T[] = [];

  for (const field of fields) {
    const rings = drawableRings(field.boundary);
    const bounds = rings && boundsOfRings(rings);
    if (rings === null || bounds === null) skipped.push(field);
    else drawable.push({ ...field, rings, bounds });
  }

  return { drawable, skipped };
}

/**
 * A Field's camera target: the centre of its box. 113586 §4j pans and zooms to the Field the
 * stepping controls move to, and `centerAt` on the adapter takes a point, not a box — this is the
 * conversion, and it lives here beside the arithmetic that produced the box.
 *
 * ponytail: plain midpoint, no antimeridian wrap — the same reasoning `boundsIntersect` records.
 */
export function centerOfBounds({ north, south, east, west }: MapBounds): MapPoint {
  return { lat: (north + south) / 2, lon: (east + west) / 2 };
}
